'use client';

import React, { useEffect } from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { RotateCcw, Home, AlertTriangle } from 'lucide-react';
import { MenuPublico } from '@/shared/components/layout/MenuPublico';
import { Button } from '@/shared/components/ui';

interface ErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function GlobalError({ error, reset }: ErrorProps) {
  useEffect(() => {
    // Registrar el error para diagnóstico
    console.error('Unhandled runtime error:', error);
  }, [error]);

  return (
    <div
      style={{
        minHeight: '100vh',
        width: '100%',
        backgroundColor: '#EEF1F8',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: "var(--font-nunito, 'Nunito', sans-serif)",
      }}
    >
      {/* Menú Superior Oficial */}
      <MenuPublico />

      {/* Contenido Principal Centrado */}
      <main
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem 1.5rem',
          textAlign: 'center',
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
          style={{
            maxWidth: '520px',
            width: '100%',
            backgroundColor: '#ffffff',
            borderRadius: '2rem',
            padding: '3rem 2.5rem',
            boxShadow: '0 20px 40px -15px rgba(28, 25, 56, 0.08), 0 0 1px rgba(0,0,0,0.1)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {/* Icono de Alerta Cálido */}
          <div
            style={{
              width: '72px',
              height: '72px',
              borderRadius: '9999px',
              backgroundColor: '#fff1f2',
              border: '2px solid #fecdd3',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#e11d48',
              marginBottom: '1.5rem',
              boxShadow: '0 8px 16px -4px rgba(225, 29, 72, 0.15)',
            }}
          >
            <AlertTriangle size={36} strokeWidth={2.3} />
          </div>

          <h1
            style={{
              fontSize: '1.65rem',
              fontWeight: 800,
              color: '#1C1938',
              marginBottom: '0.75rem',
              lineHeight: 1.3,
            }}
          >
            ¡Ups! Algo no salió como esperábamos
          </h1>

          <p
            style={{
              fontSize: '0.95rem',
              color: '#64748B',
              lineHeight: 1.5,
              marginBottom: '1.75rem',
              maxWidth: '420px',
            }}
          >
            Ha ocurrido un problema inesperado al cargar la página. No te preocupes, tus datos están a salvo.
          </p>

          {error.digest && (
            <div
              style={{
                fontSize: '0.75rem',
                fontFamily: 'monospace',
                color: '#94A3B8',
                backgroundColor: '#F8FAFC',
                padding: '0.4rem 0.8rem',
                borderRadius: '9999px',
                marginBottom: '1.75rem',
              }}
            >
              Código de referencia: {error.digest}
            </div>
          )}

          {/* Acciones de Recuperación */}
          <div
            style={{
              display: 'flex',
              gap: '1rem',
              width: '100%',
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Button
              type="button"
              variant="outline"
              size="lg"
              leftIcon={<RotateCcw size={17} />}
              onClick={() => reset()}
              style={{
                borderRadius: '9999px',
                borderColor: '#78A83B',
                color: '#78A83B',
                fontWeight: 800,
                flex: '1 1 160px',
              }}
            >
              Reintentar
            </Button>

            <Link href="/" style={{ textDecoration: 'none', flex: '1 1 160px' }}>
              <Button
                type="button"
                variant="primary"
                size="lg"
                leftIcon={<Home size={17} />}
                style={{
                  width: '100%',
                  borderRadius: '9999px',
                  backgroundColor: '#78A83B',
                  color: '#ffffff',
                  fontWeight: 800,
                }}
              >
                Volver al Inicio
              </Button>
            </Link>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
